import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, OTPInput, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLayout, useProfile } from "@/hooks";
import { useLazyVerifyPhoneRequestQuery, useVerifyPhoneMutation } from "@/redux/apis/profile/profileApi";
import { addToast } from "@/utils";

type Step = "verify" | "sent" | "success";

interface VerificationState {
  step: Step;
  setStep: (step: Step) => void;
}

const VerificationContext = createContext<VerificationState>({} as VerificationState);

export const ProfilePhoneVerificationForm = () => {
  const [step, setStep] = useState<Step>("verify");

  const renderSteps = () => {
    switch (step) {
      case "sent":
        return <Sent />;

      case "success":
        return <Success />;

      default:
        return <Verify />;
    }
  };

  return (
    <div className="flex min-h-full max-w-lg flex-1 flex-col items-center justify-center px-8">
      <VerificationContext.Provider value={{ setStep, step }}>{renderSteps()}</VerificationContext.Provider>
    </div>
  );
};

const Verify = () => {
  const { setFullScreen } = useLayout();
  const navigate = useNavigate();
  const { setStep } = useContext(VerificationContext);
  const [requestMobileVerification, { isFetching }] = useLazyVerifyPhoneRequestQuery();

  const handleBack = () => {
    setFullScreen(false);
    navigate("../../profile");
  };

  const handleClick = () => {
    requestMobileVerification()
      .unwrap()
      .then(() => setStep("sent"));
  };

  return (
    <div className="mt-8 flex flex-col items-center gap-3 text-center">
      <Icon name="smartphone" isCustom size={128} className="inline" />
      <Typography variant="h1" className="-mt-4">
        OTP Verification
      </Typography>
      <Typography variant="paragraph" className="mb-4 mt-2 max-w-md text-center text-neutral-dark-gray">
        We will send you a One Time Password on this mobile number
      </Typography>
      <Button variant="primary" size="lg" onClick={handleClick} disabled={isFetching}>
        {isFetching ? <Spinner /> : "Get OTP"}
      </Button>
      <Button variant="tertiary" size="lg" onClick={handleBack} disabled={isFetching}>
        <Icon name="ArrowLeft" className="mr-2 inline" />
        Back to Profile
      </Button>
    </div>
  );
};

const Sent = () => {
  const profile = useProfile();
  const { setStep } = useContext(VerificationContext);
  const [verifyPhone, { isLoading }] = useVerifyPhoneMutation();
  const [otp, setOtp] = useState("");

  const handleClick = () => {
    if (!otp) return;

    verifyPhone(otp)
      .unwrap()
      .then(() => setStep("success"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  return (
    <div className="mt-8 flex flex-col items-center gap-3 text-center">
      <Icon name="smartphone-sent" isCustom size={128} className="inline" />
      <Typography variant="h1" className="-mt-4">
        OTP Verification
      </Typography>
      <Typography variant="paragraph" className="mb-2 mt-2 max-w-sm text-center text-neutral-dark-gray">
        We sent a verification link to {profile.mobile}
      </Typography>
      <OTPInput value={otp} onChange={setOtp} className="mb-4" />
      <Button variant="primary" size="lg" onClick={handleClick} disabled={isLoading} className="w-full">
        {isLoading ? <Spinner /> : "Verify your mobile"}
      </Button>
      <Typography className="mt-4">
        Didn&apos;t receive an OTP?{" "}
        <span className="cursor-pointer text-info" onClick={() => setStep("verify")}>
          Click to resend
        </span>
      </Typography>
    </div>
  );
};

const Success = () => {
  const { setFullScreen } = useLayout();
  const navigate = useNavigate();

  const handleBack = () => {
    setFullScreen(false);
    navigate("../../profile?updated=1");
  };

  return (
    <div className="mt-8 flex flex-col items-center gap-3 text-center">
      <Icon name="smartphone-verified" isCustom size={128} className="inline" />
      <Typography variant="h1" className="-mt-4">
        OTP Verification
      </Typography>
      <Typography variant="paragraph" className="mb-4 mt-2 max-w-md text-center text-neutral-dark-gray">
        Your account has been fully verified. You can now use the full access of the Rideminder Web App.
      </Typography>
      <Button variant="primary" size="lg" onClick={handleBack} className="!w-full">
        Continue to Profile
      </Button>
    </div>
  );
};
